/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { KeyIcon } from '@heroicons/react/24/outline';
import { organizationService } from '../../services/api';
import ConfirmModal from '../../components/common/ConfirmModal';

export default function OrgSSOTab({ orgId }: { orgId: string }) {
  const [issuerUrl, setIssuerUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [hasSecret, setHasSecret] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await organizationService.getSSO(orgId);
      setIssuerUrl(res.data.issuer_url);
      setClientId(res.data.client_id);
      setEnabled(res.data.enabled);
      setHasSecret(res.data.has_client_secret);
      setExists(true);
    } catch {
      setExists(false); // 404 = not configured yet
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await organizationService.upsertSSO(orgId, {
        issuer_url: issuerUrl,
        client_id: clientId,
        client_secret: clientSecret || undefined,
        enabled,
      });
      toast.success('SSO connection saved');
      setClientSecret('');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await organizationService.testSSO(orgId);
      toast.success('OIDC discovery succeeded');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Discovery failed');
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await organizationService.deleteSSO(orgId);
      setIssuerUrl('');
      setClientId('');
      setClientSecret('');
      setEnabled(false);
      setExists(false);
      setHasSecret(false);
      toast.success('SSO connection removed');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to remove connection');
    } finally {
      setRemoving(false);
      setShowRemoveConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="card max-w-xl flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="card max-w-xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-500/20 rounded-lg">
          <KeyIcon className="w-5 h-5 text-primary-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Enterprise SSO</h3>
          <p className="text-sm text-gray-400">Connect your OpenID Connect identity provider</p>
        </div>
      </div>

      <p className="text-sm text-gray-400">
        Okta, Azure AD, Google Workspace, Auth0… Sign-ins from your verified domains will route here. Register this
        redirect URI with your IdP:
        <span className="block font-mono text-xs mt-2 bg-gray-800 rounded px-3 py-2 text-gray-200 overflow-x-auto">
          {`${window.location.origin}/portal/auth/sso/callback`}
        </span>
      </p>

      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Issuer URL</label>
          <input
            type="url"
            required
            value={issuerUrl}
            onChange={(e) => setIssuerUrl(e.target.value)}
            placeholder="https://your-tenant.okta.com"
            className="input font-mono"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Client ID</label>
          <input required value={clientId} onChange={(e) => setClientId(e.target.value)} className="input font-mono" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Client secret {hasSecret && <span className="text-gray-500 font-normal">(saved - leave blank to keep)</span>}
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={hasSecret ? '••••••••' : ''}
            className="input font-mono"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500 bg-gray-800 border-gray-600"
          />
          Enable enterprise SSO for verified domains
        </label>
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : 'Save connection'}
          </button>
          {exists && (
            <>
              <button type="button" onClick={test} disabled={testing} className="btn-secondary disabled:opacity-50">
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              <button type="button" onClick={() => setShowRemoveConfirm(true)} className="btn-danger">
                Remove
              </button>
            </>
          )}
        </div>
      </form>

      <ConfirmModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={remove}
        isLoading={removing}
        title="Remove SSO connection"
        message="Remove the SSO connection? Enterprise sign-in for this org will stop working."
        confirmText="Remove connection"
        variant="danger"
      />
    </div>
  );
}
